import { PDFDocument, PDFName, PDFArray, PDFRef } from "pdf-lib";
import { saveAs } from "file-saver";
import { exportInvoiceHtmlToPdfBytes } from "./exportInvoiceHtmlToPdf";

function splitFiles(filesString) {
  if (!filesString) return [];
  return String(filesString).split("|").map((s) => s.trim()).filter(Boolean);
}

function getFileUrl(path) {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  let normalized = String(path).replace(/\\/g, "/");
  normalized = normalized.replace(/^C:\/Dokumenta\//i, "");
  normalized = normalized.replace(/^\/+/, "");
  return `${process.env.REACT_APP_BACKEND_URL}/files/${encodeURI(normalized)}`;
}

function extOf(pathOrUrl) {
  const s = String(pathOrUrl).split("?")[0].toLowerCase();
  const m = s.match(/\.([a-z0-9]+)$/i);
  return m ? m[1] : "";
}

async function fetchAsArrayBuffer(url, token) {
  const headers = token ? { Authorization: "Bearer " + token } : undefined;
  const res = await fetch(url, { headers });
  if (!res.ok) throw new Error(`Ne mogu da preuzmem fajl: ${url} (${res.status})`);
  return await res.arrayBuffer();
}

function a4PortraitPoints() {
  return { w: 595.28, h: 841.89 };
}

function isRef(v) { return v instanceof PDFRef; }

function getStreamLen(doc, obj) {
  const ctx = doc.context;
  const resolved = isRef(obj) ? ctx.lookup(obj) : obj;
  if (!resolved) return 0;
  if (resolved instanceof PDFArray) {
    let sum = 0;
    for (let i = 0; i < resolved.size(); i++) sum += getStreamLen(doc, resolved.get(i));
    return sum;
  }
  if (resolved?.contents && resolved.contents.length != null) return resolved.contents.length;
  return 0;
}

function isPageBlank(invDoc, page, threshold = 200) {
  const contents = page.node.get(PDFName.of("Contents"));
  if (!contents) return true;
  return getStreamLen(invDoc, contents) < threshold;
}

export async function exportInvoiceWithFiles({
  podaci,
  token,
  fileName = "Faktura_Merged.pdf",
  marginPt = 18,
}) {
  // 1) Generiši fakturu iz HTML template-a (Puppeteer na backend-u)
  const invoiceBytes = await exportInvoiceHtmlToPdfBytes(podaci, token);

  // 2) Master PDF
  const master = await PDFDocument.create();

  // Dodaj fakturu (preskoči prazne strane)
  {
    const invDoc = await PDFDocument.load(invoiceBytes);
    const indices = [];
    for (const i of invDoc.getPageIndices()) {
      if (!isPageBlank(invDoc, invDoc.getPage(i), 200)) indices.push(i);
    }
    const safe = indices.length ? indices : [0];
    const pages = await master.copyPages(invDoc, safe);
    pages.forEach((p) => master.addPage(p));
  }

  // 3) Attachments iz Files polja
  const files = splitFiles(podaci?.Files);
  for (const f of files) {
    const url = getFileUrl(f);
    if (!url) continue;

    const ab = await fetchAsArrayBuffer(url, token);
    const ext = extOf(url);

    if (ext === "pdf") {
      const doc = await PDFDocument.load(ab);
      const indices = [];
      for (const i of doc.getPageIndices()) {
        if (!isPageBlank(doc, doc.getPage(i), 200)) indices.push(i);
      }
      const safe = indices.length ? indices : doc.getPageIndices();
      const pages = await master.copyPages(doc, safe);
      pages.forEach((pg) => master.addPage(pg));
      continue;
    }

    if (ext === "jpg" || ext === "jpeg" || ext === "png") {
      const { w, h } = a4PortraitPoints();
      const page = master.addPage([w, h]);
      const img = ext === "png" ? await master.embedPng(ab) : await master.embedJpg(ab);
      const maxW = w - 2 * marginPt;
      const maxH = h - 2 * marginPt;
      const scale = Math.min(maxW / img.width, maxH / img.height);
      const drawW = img.width * scale;
      const drawH = img.height * scale;
      page.drawImage(img, { x: (w - drawW) / 2, y: (h - drawH) / 2, width: drawW, height: drawH });
      continue;
    }
  }

  // 4) Snimi
  const mergedBytes = await master.save();
  saveAs(new Blob([mergedBytes], { type: "application/pdf" }), fileName);
}
